/**
 * A deliberately small GeoStyler -> QGIS vector-renderer writer.
 *
 * QLR/QML styling is QGIS XML, not OGC SLD.  SLD remains useful for the
 * downloadable offline package, but must never be spliced into a QLR maplayer.
 * This writer covers the GeoStyler symbolizers CultureMap persists for vector
 * datasets: Mark, Icon (embedded when the caller has fetched it, else a visible
 * fallback marker), Line, Fill and Text (as QGIS labeling).
 */
import type { Filter, RasterSymbolizer, Rule, Style, Symbolizer } from "geostyler-style";

type GeometryType = "point" | "line" | "polygon";

function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function colour(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const hex = value.trim().match(/^#([\da-f]{3}|[\da-f]{6})([\da-f]{2})?$/i);
  if (!hex) return value;
  const expanded =
    hex[1].length === 3
      ? hex[1]
          .split("")
          .map((c) => c + c)
          .join("")
      : hex[1];
  const rgb = [0, 2, 4].map((offset) => parseInt(expanded.slice(offset, offset + 2), 16));
  const alpha = hex[2] ? parseInt(hex[2], 16) : 255;
  return `${rgb.join(",")},${alpha}`;
}

function literal(value: unknown): string {
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  return `'${String(value ?? "").replace(/'/g, "''")}'`;
}

function field(value: unknown): string {
  return `\"${String(value ?? "").replace(/\"/g, '""')}\"`;
}

/**
 * Converts the supported GeoStyler filter subset into a QGIS expression.
 *
 * NULL-safe where it matters. QGIS compares with SQL rules: `NULL = 'x'` is NULL,
 * and so is `NOT (NULL OR …)`, and a rule whose filter is NULL draws nothing.
 * MapLibre, which the web map uses, treats a missing value as simply not equal.
 * Translated literally, the category editor's default rule
 * (`["!", ["||", ==v1, ==v2, …]]`) and any `!=` rule skip every row whose field
 * is empty, so rows the web map draws in the default style vanish in QGIS.
 */
function filterToQgis(filter?: Filter): string {
  if (filter == null || filter === true) return "";
  if (filter === false || !Array.isArray(filter)) return "FALSE";
  const [op, ...args] = filter as unknown[];
  if (op === "&&" || op === "||") {
    const parts = args.map((part) => filterToQgis(part as Filter)).filter(Boolean);
    return parts.length ? `(${parts.join(op === "&&" ? " AND " : " OR ")})` : "";
  }
  if (op === "!") {
    const inner = filterToQgis(args[0] as Filter);
    // A NULL inside counts as false, as it does in MapLibre, so its negation is true.
    return inner ? `NOT coalesce(${inner}, FALSE)` : "";
  }
  if (op === "<=x<=") return `(${field(args[0])} >= ${literal(args[1])} AND ${field(args[0])} <= ${literal(args[2])})`;
  if (["==", "!=", "<", "<=", ">", ">="].includes(String(op))) {
    // `IS NOT` is QGIS's NULL-safe `!=`: an empty field is "not 'x'", as on the web map.
    const qgisOp = op === "==" ? "=" : op === "!=" ? "IS NOT" : op;
    return `${field(args[0])} ${qgisOp} ${literal(args[1])}`;
  }
  if (op === "*=") return `${field(args[0])} ILIKE ${literal(`%${String(args[1] ?? "")}%`)}`;
  console.warn(`[geostylerToQgis] Unsupported filter operator ${String(op)}; rule will be unfiltered`);
  return "";
}

function props(values: Record<string, unknown>): string {
  return Object.entries(values)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `<prop k="${esc(key)}" v="${esc(value)}"/>`)
    .join("");
}

/**
 * Sizes are written in pixels, matching the web map. QGIS reads the unit from
 * each symbol layer's own `*_unit` prop and defaults to millimetres (1 mm ≈ 3.8
 * px), which drew every line ~4× too thick — the `<symbol outputUnit>`
 * attribute does not set it.
 */
const PX = "Pixel";

/** MapLibre's defaults for the paint values a style may leave unset. */
const DEFAULT_LINE_WIDTH_PX = 1;
const DEFAULT_CIRCLE_RADIUS_PX = 5;
/** Diameter of the fallback marker drawn for an Icon at `icon-size` 1. */
const ICON_FALLBACK_DIAMETER_PX = 12;

/**
 * A fixed positive number, or `fallback`. MapLibre zoom/data expressions (for
 * example `step,zoom,...`) are valid in the web map but have no fixed value to
 * write, and would make the QGIS symbol invisible.
 */
function fixedSize(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}

// ─── Zoom curves ──────────────────────────────────────────────────────────────

/**
 * A MapLibre zoom curve as numbers: `["step", ["zoom"], base, z1, v1, …]` or
 * `["interpolate", ["linear"], ["zoom"], z1, v1, …]`. Anything else (data
 * expressions, exponential curves) has no QGIS translation here and is null.
 */
type ZoomCurve = { kind: "step"; base: number; stops: Array<[number, number]> } | { kind: "linear"; stops: Array<[number, number]> };

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

function zoomCurve(value: unknown): ZoomCurve | null {
  if (!Array.isArray(value)) return null;
  const isZoom = (v: unknown) => Array.isArray(v) && v.length === 1 && v[0] === "zoom";
  const stopsOf = (rest: unknown[]): Array<[number, number]> | null => {
    if (rest.length % 2) return null;
    const stops: Array<[number, number]> = [];
    for (let i = 0; i < rest.length; i += 2) {
      const [zoom, v] = [rest[i], rest[i + 1]];
      if (!finite(zoom) || !finite(v)) return null;
      stops.push([zoom, v]);
    }
    return stops;
  };
  if (value[0] === "step" && isZoom(value[1]) && finite(value[2])) {
    const stops = stopsOf(value.slice(3));
    return stops ? { kind: "step", base: value[2], stops } : null;
  }
  if (value[0] === "interpolate" && Array.isArray(value[1]) && value[1][0] === "linear" && isZoom(value[2])) {
    const stops = stopsOf(value.slice(3));
    return stops?.length ? { kind: "linear", stops } : null;
  }
  return null;
}

/** The curve's value at `zoom`, as MapLibre evaluates it (clamped at both ends). */
function curveAt(curve: ZoomCurve, zoom: number): number {
  if (curve.kind === "step") return curve.stops.reduce((v, [z, next]) => (zoom >= z ? next : v), curve.base);
  const { stops } = curve;
  if (zoom <= stops[0][0]) return stops[0][1];
  for (let i = 1; i < stops.length; i++) {
    const [z0, v0] = stops[i - 1];
    const [z1, v1] = stops[i];
    if (zoom <= z1) return z1 === z0 ? v1 : v0 + ((zoom - z0) / (z1 - z0)) * (v1 - v0);
  }
  return stops[stops.length - 1][1];
}

/**
 * The web map's zoom at QGIS's current map scale. Zoom 0 is 1:559 082 264, the
 * scale set QGIS itself uses for XYZ and vector tiles, and each zoom halves it.
 */
const QGIS_ZOOM = "log(2, 559082264.028717 / @map_scale)";

/** The curve as a QGIS expression of `@cm_zoom` (bound by {@link zoomSizeExpression}). */
function curveToQgis(curve: ZoomCurve): string {
  if (curve.kind === "step") {
    const cases = [...curve.stops].reverse().map(([z, v]) => `WHEN @cm_zoom >= ${z} THEN ${v}`);
    return cases.length ? `CASE ${cases.join(" ")} ELSE ${curve.base} END` : String(curve.base);
  }
  const { stops } = curve;
  const cases = [`WHEN @cm_zoom <= ${stops[0][0]} THEN ${stops[0][1]}`];
  for (let i = 1; i < stops.length; i++) {
    const [z0, v0] = stops[i - 1];
    const [z1, v1] = stops[i];
    cases.push(`WHEN @cm_zoom <= ${z1} THEN scale_linear(@cm_zoom, ${z0}, ${z1}, ${v0}, ${v1})`);
  }
  return `CASE ${cases.join(" ")} ELSE ${stops[stops.length - 1][1]} END`;
}

/** `factor × curve` at the map's current zoom, as a QGIS expression. */
function zoomExpression(curve: ZoomCurve, factor: number): string {
  return `with_variable('cm_zoom', ${QGIS_ZOOM}, ${factor} * (${curveToQgis(curve)}))`;
}

/**
 * Where QGIS evaluates no expression — the legend, the symbol preview — a
 * zoom-curved size shows as its value at this zoom: mid-range, where the curves
 * in our styles are neither at their smallest nor their largest.
 */
const REPRESENTATIVE_ZOOM = 10;

/**
 * QGIS data-defined property XML: each entry binds a property (by QGIS's own
 * name — `size` on a symbol layer, `Size` / `FontOpacity` / `BufferSize` on a
 * label) to an expression. "" when there is nothing to bind. A symbol layer
 * reads them from `<data_defined_properties>`, a label from `<dd_properties>`;
 * QGIS ignores them under the other name.
 */
function dataDefinedProperties(entries: Array<[string, string]>, element = "data_defined_properties"): string {
  if (!entries.length) return "";
  const properties = entries
    .map(
      ([name, expression]) =>
        `<Option name="${esc(name)}" type="Map"><Option name="active" type="bool" value="true"/>` +
        `<Option name="expression" type="QString" value="${esc(expression)}"/><Option name="type" type="int" value="3"/></Option>`
    )
    .join("");
  return (
    `<${element}><Option type="Map"><Option name="name" type="QString" value=""/>` +
    `<Option name="properties" type="Map">${properties}</Option><Option name="type" type="QString" value="collection"/></Option></${element}>`
  );
}

/**
 * MapLibre's `icon-size` — a scale on the image's own width — as QGIS marker
 * sizing: the fixed scale for the static `size`, and, when the web map curves
 * it by zoom, a data-defined size that follows QGIS's map scale the same way.
 * Before this, a curve fell back to scale 1 and drew every icon at the image's
 * full width (128–200 px where the web map shows 13–38 px).
 */
function iconSizing(size: unknown, baseWidth: number): { scale: number; dataDefined: string } {
  const curve = zoomCurve(size);
  if (!curve) return { scale: fixedSize(size, 1), dataDefined: "" };
  const scale = curveAt(curve, REPRESENTATIVE_ZOOM);
  return { scale: scale > 0 ? scale : 1, dataDefined: dataDefinedProperties([["size", zoomExpression(curve, baseWidth)]]) };
}

/**
 * QGIS SimpleMarker `size` is a diameter. A Mark carries MapLibre's
 * `circle-radius`, so it doubles; an Icon carries `icon-size`, a scale factor
 * on an image QGIS cannot fetch, so it scales the fallback marker instead.
 */
function markerDiameter(s: Record<string, any>): number {
  if (s.kind === "Icon") return Number((ICON_FALLBACK_DIAMETER_PX * iconSizing(s.size, 1).scale).toFixed(2));
  return 2 * fixedSize(s.radius, DEFAULT_CIRCLE_RADIUS_PX);
}

/**
 * An icon image fetched by the caller, keyed in {@link QgisStyleOptions.icons}
 * by the Icon symbolizer's `image` reference. QGIS cannot authenticate to the
 * private icon CDN, so the image travels inside the file as base64.
 */
export interface QgisEmbeddedIcon {
  /** `data:<mime>;base64,<data>` */
  dataUri: string;
  /** Natural size in pixels — MapLibre's `icon-size` is a scale on this. */
  width: number;
  height: number;
}

export interface QgisStyleOptions {
  icons?: ReadonlyMap<string, QgisEmbeddedIcon>;
}

/** The reference an Icon symbolizer's image is fetched and keyed by, if any. */
export function iconImageRef(image: unknown): string | undefined {
  if (typeof image === "string") return image.trim() || undefined;
  if (image && typeof image === "object" && typeof (image as { source?: unknown }).source === "string") {
    return (image as { source: string }).source.trim() || undefined;
  }
  return undefined;
}

/**
 * An embedded icon as a QGIS marker layer: RasterMarker for bitmaps, SvgMarker
 * for SVG. Both read a `base64:` path from inside the file. The width is the
 * image's own width × `icon-size`, as MapLibre draws it; QGIS keeps the aspect.
 */
function embeddedIconLayer(s: Record<string, any>, icon: QgisEmbeddedIcon): string {
  const match = icon.dataUri.match(/^data:([^;,]+)[^,]*;base64,(.*)$/);
  const mime = match?.[1] ?? "";
  const path = `base64:${match?.[2] ?? ""}`;
  const { scale, dataDefined } = iconSizing(s.size, icon.width);
  const width = Number((icon.width * scale).toFixed(2));
  // MapLibre scales icon-offset by icon-size.
  const offset = Array.isArray(s.offset) ? s.offset.map((v: unknown) => Number(v) * scale).join(",") : "0,0";
  const shared = { angle: s.rotate ?? 0, offset, offset_unit: PX, size: width, size_unit: PX };
  if (mime === "image/svg+xml") {
    return `<layer pass="0" class="SvgMarker" locked="0">${props({
      ...shared,
      name: path,
      fixedAspectRatio: icon.width > 0 ? icon.height / icon.width : 0
    })}${dataDefined}</layer>`;
  }
  return `<layer pass="0" class="RasterMarker" locked="0">${props({
    ...shared,
    imageFile: path,
    // 0 = keep the image's own aspect ratio.
    fixedAspectRatio: 0,
    alpha: 1
  })}${dataDefined}</layer>`;
}

function symbolXml(symbolizer: Symbolizer | undefined, geometry: GeometryType, name: number, options: QgisStyleOptions = {}): string {
  const s = (symbolizer ?? { kind: "Mark" }) as Record<string, any>;
  if (geometry === "line") {
    const width = fixedSize(s.width, DEFAULT_LINE_WIDTH_PX);
    // MapLibre measures dashes in line widths; QGIS in the dash unit.
    const dashes = Array.isArray(s.dasharray) ? s.dasharray.map((d: unknown) => Number(d) * width) : undefined;
    return `<symbol outputUnit="Pixel" alpha="${esc(s.opacity ?? 1)}" type="line" name="${name}"><layer pass="0" class="SimpleLine" locked="0">${props(
      {
        // MapLibre's defaults are butt caps and miter joins; QGIS calls butt "flat".
        capstyle: s.cap === "round" || s.cap === "square" ? s.cap : "flat",
        color: colour(s.color, "59,130,246,255"),
        joinstyle: s.join === "round" || s.join === "bevel" ? s.join : "miter",
        offset: 0,
        penstyle: "solid",
        use_custom_dash: dashes ? 1 : 0,
        customdash: dashes?.join(";"),
        customdash_unit: PX,
        width,
        width_unit: PX,
        offset_unit: PX
      }
    )}</layer></symbol>`;
  }
  if (geometry === "polygon") {
    return `<symbol outputUnit="Pixel" alpha="${esc(s.opacity ?? 1)}" type="fill" name="${name}"><layer pass="0" class="SimpleFill" locked="0">${props(
      {
        color: colour(s.color, "59,130,246,128"),
        color_border: colour(s.outlineColor, "35,35,35,255"),
        offset: "0,0",
        style: "solid",
        style_border: "solid",
        // MapLibre draws a fill's outline at 1 px.
        width_border: fixedSize(s.outlineWidth, 1),
        border_width_unit: PX,
        offset_unit: PX
      }
    )}</layer></symbol>`;
  }
  // QGIS cannot authenticate to our private icon CDN, so an Icon is drawn from
  // the image the caller fetched and embedded; one that could not be fetched
  // falls back to a coloured SimpleMarker rather than an invisible remote image.
  const iconRef = s.kind === "Icon" ? iconImageRef(s.image) : undefined;
  const icon = iconRef ? options.icons?.get(iconRef) : undefined;
  if (icon) {
    return `<symbol outputUnit="Pixel" alpha="${esc(s.opacity ?? 1)}" type="marker" name="${name}">${embeddedIconLayer(s, icon)}</symbol>`;
  }
  const isMark = s.kind === "Mark";
  const strokeWidth = isMark && typeof s.strokeWidth === "number" && s.strokeWidth > 0 ? s.strokeWidth : 0;
  return `<symbol outputUnit="Pixel" alpha="${esc(s.opacity ?? s.fillOpacity ?? 1)}" type="marker" name="${name}"><layer pass="0" class="SimpleMarker" locked="0">${props(
    {
      angle: s.rotate ?? 0,
      color: colour(s.color, isMark ? "59,130,246,255" : "59,130,246,255"),
      color_border: colour(s.strokeColor, "35,35,35,255"),
      name: isMark ? (s.wellKnownName ?? "circle") : "circle",
      offset: Array.isArray(s.offset) ? s.offset.join(",") : "0,0",
      offset_unit: PX,
      // QGIS draws width 0 as a hairline; MapLibre draws no stroke.
      outline_style: strokeWidth > 0 ? "solid" : "no",
      outline_width: strokeWidth,
      outline_width_unit: PX,
      size: markerDiameter(s),
      size_unit: PX
    }
  )}${s.kind === "Icon" ? iconSizing(s.size, ICON_FALLBACK_DIAMETER_PX).dataDefined : ""}</layer></symbol>`;
}

function compatibleSymbolizer(rule: Rule, geometry: GeometryType): Symbolizer | undefined {
  const desired = geometry === "point" ? ["Icon", "Mark"] : geometry === "line" ? ["Line"] : ["Fill"];
  return rule.symbolizers.find((symbolizer) => desired.includes(symbolizer.kind));
}

/** Returns a native QGIS renderer-v2 block for a vector layer. */
export function geostylerToQgisRenderer(style: Style | undefined, geometry: GeometryType, options: QgisStyleOptions = {}): string {
  const rules = style?.rules ?? [];
  const renderable = rules.map((rule) => ({ rule, symbolizer: compatibleSymbolizer(rule, geometry) })).filter((item) => item.symbolizer);
  if (!renderable.length) {
    // A labels-only style draws no symbol on the web map; QGIS would otherwise
    // add its default marker under every label.
    if (rules.some((rule) => rule.symbolizers.some((symbolizer) => symbolizer.kind === "Text"))) {
      return `<renderer-v2 type="nullSymbol"/>`;
    }
    return `<renderer-v2 symbollevels="0" type="singleSymbol"><symbols>${symbolXml(undefined, geometry, 0)}</symbols><rotation field=""/><sizescale field=""/></renderer-v2>`;
  }
  if (renderable.length === 1 && !renderable[0].rule.filter) {
    return `<renderer-v2 symbollevels="0" type="singleSymbol"><symbols>${symbolXml(renderable[0].symbolizer, geometry, 0, options)}</symbols><rotation field=""/><sizescale field=""/></renderer-v2>`;
  }
  // A rule with no GeoStyler filter (the category editor's "default" rule) must
  // become QGIS's own catch-all, `filter="ELSE"` — an empty `filter=""` attribute
  // means "match every feature" to QGIS's RuleRenderer, not "unfiltered fallback".
  // Left as-is, the default rule's symbol paints over every category rule's
  // symbol on every feature (two 50%-opacity fills blend into one wrong colour)
  // instead of only the features no category rule matched. Only applies once
  // there is at least one real filter to fall back FROM; a set of rules that are
  // all unfiltered has nothing for ELSE to be an alternative to.
  const anyRuleFiltered = renderable.some(({ rule }) => !!rule.filter);
  const qgisRules = renderable
    .map(({ rule }, index) => {
      const filterAttr = rule.filter ? filterToQgis(rule.filter) : anyRuleFiltered ? "ELSE" : "";
      return `<rule scalemaxdenom="${esc(rule.scaleDenominator?.max ?? 0)}" scalemindenom="${esc(rule.scaleDenominator?.min ?? 0)}" filter="${esc(filterAttr)}" symbol="${index}" label="${esc(rule.name)}"/>`;
    })
    .join("");
  const symbols = renderable.map(({ symbolizer }, index) => symbolXml(symbolizer, geometry, index, options)).join("");
  return `<renderer-v2 symbollevels="0" type="RuleRenderer" firstrule="0"><rules>${qgisRules}</rules><symbols>${symbols}</symbols></renderer-v2>`;
}

// ─── Labels ───────────────────────────────────────────────────────────────────

/** MapLibre's defaults for the text paint/layout values a style may leave unset. */
const DEFAULT_TEXT_SIZE_PX = 16;
const DEFAULT_TEXT_MAX_WIDTH_EM = 10;

/**
 * A GeoStyler label as a QGIS expression, or undefined when there is nothing a
 * fixed expression can express. Templates (`"{{name}} ({{year}})"`, the form the
 * style editor writes) become a `concat()` so a NULL field blanks only its own
 * part, as MapLibre does; `["get", field]` becomes the field.
 */
function labelExpression(label: unknown): string | undefined {
  if (Array.isArray(label) && label[0] === "get" && typeof label[1] === "string") return field(label[1]);
  if (typeof label !== "string" || !label.trim()) return undefined;
  const parts: string[] = [];
  let last = 0;
  for (const m of label.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
    if (m.index! > last) parts.push(literal(label.slice(last, m.index)));
    parts.push(field(m[1]));
    last = m.index! + m[0].length;
  }
  if (last < label.length) parts.push(literal(label.slice(last)));
  return parts.length === 1 ? parts[0] : `concat(${parts.join(", ")})`;
}

/** `"Open Sans Bold Italic"` → family "Open Sans", bold, italic — MapLibre font stacks name the style in the font. */
function fontFace(font: unknown): { family: string; bold: boolean; italic: boolean } {
  const first = Array.isArray(font) && typeof font[0] === "string" ? font[0] : "Open Sans Regular";
  const words = first.split(/\s+/);
  const styleWords = /^(regular|bold|semibold|medium|light|italic|oblique)$/i;
  while (words.length > 1 && styleWords.test(words[words.length - 1])) words.pop();
  return { family: words.join(" "), bold: /bold/i.test(first), italic: /italic|oblique/i.test(first) };
}

/**
 * A label paint/layout value that may be a zoom curve: the fixed value QGIS
 * shows where it evaluates nothing (see {@link REPRESENTATIVE_ZOOM}), and the
 * curve when there is one. A fixed number, or anything untranslatable, is
 * `fixed(value)` with no curve.
 */
function labelValue(value: unknown, fixed: (v: unknown) => number): { value: number; curve: ZoomCurve | null } {
  const curve = zoomCurve(value);
  return curve ? { value: curveAt(curve, REPRESENTATIVE_ZOOM), curve } : { value: fixed(value), curve: null };
}

/**
 * The zoom from which a curve is above 0, or undefined when it is from the
 * start. Our label curves rise with zoom (size and opacity 0 when zoomed out),
 * so this is where the web map starts drawing them.
 */
function visibleFromZoom(curve: ZoomCurve | null): number | undefined {
  if (!curve) return undefined;
  if (curve.kind === "step") return curve.base > 0 ? undefined : curve.stops.find(([, v]) => v > 0)?.[0];
  const firstShown = curve.stops.findIndex(([, v]) => v > 0);
  return firstShown <= 0 ? undefined : curve.stops[firstShown - 1][0];
}

/** The map scale at a web map zoom — {@link QGIS_ZOOM} inverted. */
const scaleAtZoom = (zoom: number) => Math.round(559082264.028717 / 2 ** zoom);

/** A QGIS `<settings>` block (one label style) for a Text symbolizer. */
function labelSettingsXml(text: Record<string, any>, expression: string, geometry: GeometryType): string {
  // Size, opacity and halo are often zoom curves in our styles (0 when zoomed
  // out, growing as you zoom in). Written as fixed values, QGIS drew every label
  // full-size at every zoom and buried the map under text when zoomed out.
  const sizing = labelValue(text.size, (v) => fixedSize(v, DEFAULT_TEXT_SIZE_PX));
  const size = sizing.value > 0 ? sizing.value : DEFAULT_TEXT_SIZE_PX;
  const opacity = labelValue(text.opacity, fixedOpacity);
  const halo = labelValue(text.haloWidth, (v) => (typeof v === "number" && v > 0 ? v : 0));
  const haloWidth = Math.max(halo.value, 0);
  const { family, bold, italic } = fontFace(text.font);
  // MapLibre offsets and wrap width are in ems of the text size.
  const [dx, dy] = Array.isArray(text.offset) ? text.offset.map((v: unknown) => (Number(v) || 0) * size) : [0, 0];
  const maxWidthEm = fixedSize(text.maxWidth, DEFAULT_TEXT_MAX_WIDTH_EM);
  // Where the web map draws no label (size or opacity 0 below some zoom), QGIS
  // hides it too: scaleMax is the most zoomed-out scale that still labels.
  const shownFrom = Math.max(visibleFromZoom(sizing.curve) ?? -Infinity, visibleFromZoom(opacity.curve) ?? -Infinity);
  const scaleVisibility = Number.isFinite(shownFrom)
    ? `scaleVisibility="1" scaleMin="0" scaleMax="${scaleAtZoom(shownFrom)}"`
    : `scaleVisibility="0"`;
  const curved = dataDefinedProperties(
    [
      ...(sizing.curve ? ([["Size", zoomExpression(sizing.curve, 1)]] as Array<[string, string]>) : []),
      // QGIS label opacity is 0–100.
      ...(opacity.curve ? ([["FontOpacity", zoomExpression(opacity.curve, 100)]] as Array<[string, string]>) : []),
      ...(halo.curve ? ([["BufferSize", zoomExpression(halo.curve, 1)]] as Array<[string, string]>) : [])
    ],
    "dd_properties"
  );
  const caps = text.transform === "uppercase" ? 1 : text.transform === "lowercase" ? 2 : 0;
  // 1 = over point / polygon centroid (MapLibre's default "point" placement);
  // 2 = parallel to a line; 3 = curved along it (MapLibre's "line").
  const placement = geometry === "line" ? (text.placement === "line" || text.placement === "line-center" ? 3 : 2) : 1;

  return (
    `<settings calloutType="simple">` +
    `<text-style fieldName="${esc(expression)}" isExpression="1" fontFamily="${esc(family)}" ` +
    `fontSize="${size}" fontSizeUnit="Pixel" fontWeight="${bold ? 75 : 50}" fontItalic="${italic ? 1 : 0}" ` +
    `fontCapitals="${caps}" textColor="${colour(text.color, "0,0,0,255")}" textOpacity="${esc(clamp(opacity.value, 0, 1))}" ` +
    `multilineHeight="1.2" multilineHeightUnit="Percentage" allowHtml="0">` +
    `<families/>` +
    `<text-buffer bufferDraw="${haloWidth > 0 || halo.curve ? 1 : 0}" bufferSize="${haloWidth}" bufferSizeUnits="Pixel" ` +
    `bufferColor="${colour(text.haloColor, "255,255,255,255")}" bufferOpacity="1" bufferNoFill="1" bufferJoinStyle="128"/>` +
    `<background shapeDraw="0"/><shadow shadowDraw="0"/><substitutions/>` +
    `</text-style>` +
    `<text-format wrapChar="" autoWrapLength="${Math.round(maxWidthEm * 2)}" useMaxLineLengthForAutoWrap="1" multilineAlign="1"/>` +
    `<placement placement="${placement}" quadOffset="4" xOffset="${dx}" yOffset="${dy}" offsetUnits="Pixel" ` +
    `dist="0" distUnits="Pixel" centroidWhole="0" centroidInside="1" ` +
    `overlapHandling="${text.allowOverlap ? "AllowOverlapAnywhere" : "PreventOverlap"}"/>` +
    `<rendering obstacle="1" obstacleType="1" drawLabels="1" fontLimitPixelSize="0" ${scaleVisibility} upsidedownLabels="0"/>` +
    curved +
    `</settings>`
  );
}

function fixedOpacity(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? clamp(value, 0, 1) : 1;
}

/**
 * The style's Text symbolizers as a QGIS `<labeling>` block, or "" when it has
 * none (the caller then leaves labels off). One unfiltered rule becomes simple
 * labeling; anything else is rule-based, with the same filter and ELSE handling
 * as the renderer so each category keeps its own label style.
 */
export function geostylerToQgisLabeling(style: Style | undefined, geometry: GeometryType): string {
  const labelled = (style?.rules ?? []).flatMap((rule) => {
    const text = rule.symbolizers.find((symbolizer) => symbolizer.kind === "Text") as Record<string, any> | undefined;
    const expression = text && labelExpression(text.label);
    return text && expression ? [{ rule, text, expression }] : [];
  });
  if (!labelled.length) return "";
  if (labelled.length === 1 && !labelled[0].rule.filter && !labelled[0].rule.scaleDenominator) {
    const { text, expression } = labelled[0];
    return `<labeling type="simple">${labelSettingsXml(text, expression, geometry)}</labeling>`;
  }
  const anyRuleFiltered = labelled.some(({ rule }) => !!rule.filter);
  const rules = labelled
    .map(({ rule, text, expression }, index) => {
      const filterAttr = rule.filter ? filterToQgis(rule.filter) : anyRuleFiltered ? "ELSE" : "";
      return (
        `<rule key="{label-${index}}" description="${esc(rule.name)}" filter="${esc(filterAttr)}" ` +
        `scalemaxdenom="${esc(rule.scaleDenominator?.max ?? 0)}" scalemindenom="${esc(rule.scaleDenominator?.min ?? 0)}">` +
        `${labelSettingsXml(text, expression, geometry)}</rule>`
      );
    })
    .join("");
  return `<labeling type="rule-based"><rules key="{label-root}">${rules}</rules></labeling>`;
}

/**
 * Band metadata as `qgis-export` returns it: the COG's `bands`, or the original
 * upload's `raster.source.bands`. `color_interp` exists for rasters converted
 * since 28 Sep 2026.
 */
export interface QgisRasterBand {
  data_type: string;
  color_interp?: string | null;
  nodata?: number | null;
}

/** How a raster file stores transparency, as the conversion worker reports it. */
export type QgisRasterMask = "per_dataset" | "alpha_band" | "nodata" | "none";

/** What the worker recorded about one raster file: the COG, or the original upload. */
export interface QgisRasterFileLayout {
  mask?: QgisRasterMask | null;
  /**
   * The band QGIS uses as alpha when it opens the file: N+1 for an internal mask
   * (QGIS adds it as an extra band), the alpha band's own index, or null when the
   * file has no alpha band (nodata, or nothing to hide).
   */
  qgis_alpha_band?: number | null;
}

/** `qgis-export`'s `raster` block; null for rasters converted before the worker recorded it. */
export interface QgisRasterMetadata {
  cog?: (QgisRasterFileLayout & { encoding?: "rgb8" | "raw" | null }) | null;
  source?:
    | (QgisRasterFileLayout & {
        bands?: QgisRasterBand[] | null;
        paletted?: boolean | null;
        /** The linear stretch a single-band original's grey display COG was baked with. */
        display_stretch?: { min?: number | null; max?: number | null } | null;
      })
    | null;
}

export interface QgisRasterPipeOptions {
  target: "live" | "offline";
  /** The COG's `bands` — what a live `.qlr` opens. An offline style reads `raster.source` instead. */
  bands?: QgisRasterBand[] | null;
  raster?: QgisRasterMetadata | null;
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Web contrast (-1…1) as QGIS contrast (-100…100), matched on the factor each
 * engine actually applies rather than scaled linearly. MapLibre multiplies by
 * `1 / (1 - c)` above zero and `1 + c` below; QGIS by `((q + 100) / 100)²`. So
 * q = 100·√factor − 100 — exact up to c = 0.75 (4×), where QGIS tops out.
 */
function qgisContrast(c: number): number {
  if (c >= 1) return 100;
  const factor = c > 0 ? 1 / (1 - c) : 1 + Math.max(c, -1);
  return Math.round(clamp(100 * Math.sqrt(factor) - 100, -100, 100));
}

/**
 * The band QGIS uses as alpha for a file, or undefined when nothing says.
 *
 * A QGIS raster renderer names its alpha band, and -1 means none. The worker's
 * COGs keep the area around the image black and hide it with an internal mask,
 * which QGIS exposes as an extra band only when it opens the file — `bands`
 * cannot show it. A renderer that says -1 over such a file paints the black; one
 * that names a band the file lacks hides the whole layer. So a renderer is
 * written only when this returns a band.
 */
function alphaBandOf(file: QgisRasterFileLayout | null | undefined, bands: QgisRasterBand[] | null | undefined): number | undefined {
  if (typeof file?.qgis_alpha_band === "number" && file.qgis_alpha_band > 0) return file.qgis_alpha_band;
  if (file?.mask === "nodata" || file?.mask === "none") return -1;
  if (file?.mask) return undefined;
  // No mask reported (the worker does not report the original's yet): a real
  // alpha band still shows in the colour interpretations, and a nodata value
  // hides its own pixels whatever the alpha band.
  const alpha = bands?.findIndex((b) => b.color_interp === "alpha") ?? -1;
  if (alpha >= 0) return alpha + 1;
  if (bands?.some((b) => b.nodata != null)) return -1;
  return undefined;
}

/**
 * What a renderer draws, from a file's bands minus its alpha band:
 *   - "rgb8": three 8-bit colour bands. QGIS draws them 0–255, no stretch.
 *   - "gray" / "rgb": one or three bands of anything else. Verified in QGIS 4.2.1:
 *     QGIS computes no stretch for a renderer it reads from a .qlr/.qml, so these
 *     get one — fixed to the worker's display stretch when it is known, else
 *     computed from the canvas as QGIS draws.
 *   - null: nothing a renderer can say safely (a lone 8-bit band may be paletted;
 *     other band counts have no colour mapping).
 */
type RasterKind = "rgb8" | "gray" | "rgb";

function rasterKind(
  bands: QgisRasterBand[] | null | undefined,
  alphaBand: number,
  opts: { encoding?: string | null; paletted?: boolean | null }
): RasterKind | null {
  const colour = (bands ?? []).filter((b, i) => b.color_interp !== "alpha" && i + 1 !== alphaBand);
  const eightBit = colour.every((b) => b.data_type === "uint8");
  if (colour.length === 3) return eightBit && opts.encoding !== "raw" ? "rgb8" : "rgb";
  if (colour.length === 1 && !(eightBit && opts.paletted !== false)) return "gray";
  return null;
}

type RasterLayout = { kind: RasterKind; alphaBand: number; stretch?: { min: number; max: number } };

/**
 * The renderer layout of the file a style lands on — the COG for a live `.qlr`,
 * the original upload for an offline `.qml` — or null when no renderer is safe.
 */
function rasterLayout(opts: QgisRasterPipeOptions): RasterLayout | null {
  if (opts.target === "live") {
    const cog = opts.raster?.cog;
    const alphaBand = alphaBandOf(cog, opts.bands);
    const kind = alphaBand === undefined ? null : rasterKind(opts.bands, alphaBand, { encoding: cog?.encoding });
    return kind && alphaBand !== undefined ? { kind, alphaBand } : null;
  }
  // An offline style is applied to the original, which only `raster.source`
  // describes: `bands` is the COG, and a DEM's COG is 3 × uint8 grey, an RGBA
  // original's is RGB + mask.
  const source = opts.raster?.source;
  if (!source || source.paletted) return null;
  const alphaBand = alphaBandOf(source, source.bands);
  const kind = alphaBand === undefined ? null : rasterKind(source.bands, alphaBand, { paletted: source.paletted });
  if (!kind || alphaBand === undefined) return null;
  const lo = source.display_stretch?.min;
  const hi = source.display_stretch?.max;
  const stretch = kind === "gray" && typeof lo === "number" && typeof hi === "number" && hi > lo ? { min: lo, max: hi } : undefined;
  return { kind, alphaBand, stretch };
}

const CANVAS_STRETCH =
  `<minMaxOrigin><limits>CumulativeCut</limits><extent>UpdatedCanvas</extent><statAccuracy>Estimated</statAccuracy>` +
  `<cumulativeCutLower>0.02</cumulativeCutLower><cumulativeCutUpper>0.98</cumulativeCutUpper><stdDevFactor>2</stdDevFactor></minMaxOrigin>`;
const NO_STRETCH =
  `<minMaxOrigin><limits>None</limits><extent>WholeRaster</extent><statAccuracy>Estimated</statAccuracy>` +
  `<cumulativeCutLower>0.02</cumulativeCutLower><cumulativeCutUpper>0.98</cumulativeCutUpper><stdDevFactor>2</stdDevFactor></minMaxOrigin>`;
const stretch = (tag: string) => `<${tag}><algorithm>StretchToMinimumMaximum</algorithm></${tag}>`;
const BILINEAR =
  `<rasterresampler maxOversampling="2" zoomedInResampler="bilinear" zoomedOutResampler="bilinear"/>` +
  `<resamplingStage>resamplingFilter</resamplingStage>`;

function rasterRendererXml(layout: RasterLayout, opacity: number): string {
  const open = (type: string, extra: string) =>
    `<rasterrenderer type="${type}" opacity="${opacity}" alphaBand="${layout.alphaBand}" ${extra} nodataColor="">`;
  if (layout.kind === "gray") {
    // The worker's display stretch reproduces the web map's grey; without it
    // QGIS computes one from the canvas.
    const enhancement = layout.stretch
      ? `<contrastEnhancement><minValue>${layout.stretch.min}</minValue><maxValue>${layout.stretch.max}</maxValue>` +
        `<algorithm>StretchToMinimumMaximum</algorithm></contrastEnhancement>`
      : stretch("contrastEnhancement");
    return (
      `${open("singlebandgray", `grayBand="1" gradient="BlackToWhite"`)}<rasterTransparency/>` +
      `${layout.stretch ? NO_STRETCH : CANVAS_STRETCH}${enhancement}</rasterrenderer>`
    );
  }
  const rgbBands = `redBand="1" greenBand="2" blueBand="3"`;
  if (layout.kind === "rgb") {
    return (
      `${open("multibandcolor", rgbBands)}<rasterTransparency/>${CANVAS_STRETCH}` +
      `${stretch("redContrastEnhancement")}${stretch("greenContrastEnhancement")}${stretch("blueContrastEnhancement")}</rasterrenderer>`
    );
  }
  return `${open("multibandcolor", rgbBands)}<rasterTransparency/>${NO_STRETCH}</rasterrenderer>`;
}

/**
 * The style editor's raster properties as a QGIS raster <pipe>, or "" when the
 * file should carry none.
 *
 * Brightness, contrast, saturation and smoothing work on the image QGIS has
 * already rendered, so they never touch transparency and are written whenever
 * the style has them. A renderer is what carries opacity — QGIS keeps a raster's
 * opacity on its renderer and ignores <layerOpacity> — and the stretch a
 * non-8-bit raster needs; but a renderer also names the alpha band, so it is
 * written only when {@link rasterLayout} knows that band. Otherwise QGIS keeps
 * its own renderer, which finds the alpha band itself, and opacity is left to
 * the user in QGIS.
 *   - 8-bit RGB(A): a renderer only to carry opacity.
 *   - everything else: the stretch renderer whenever the layer is live or styled.
 *
 * Resampling: the web map smooths ("linear") unless told "nearest"; QGIS
 * defaults to nearest-neighbour, so smoothing is written as bilinear. The live
 * layer always carries it; an offline style file carries it when it is written
 * anyway or the style asks for "linear" explicitly — a file just to restate the
 * web default is not worth replacing "Download original" for.
 *
 * Hue rotation has no QGIS equivalent and is dropped (`qgis_styled` carries it);
 * fade duration is a web map tile animation with nothing to carry.
 */
export function geostylerToQgisRasterPipe(symbolizer: RasterSymbolizer | undefined, opts: QgisRasterPipeOptions): string {
  const s = (symbolizer ?? {}) as Record<string, unknown>;

  const saturation = Math.round(clamp(finiteNumber(s.saturation, 0), -1, 1) * 100);
  const contrast = qgisContrast(finiteNumber(s.contrast, 0));
  const brightnessMin = clamp(finiteNumber(s.brightnessMin, 0), 0, 1);
  const brightnessMax = clamp(finiteNumber(s.brightnessMax, 1), 0, 1);
  const brightness = Math.round(clamp(((brightnessMin + brightnessMax) / 2 - 0.5) * 255, -255, 255));
  const opacity = Number(clamp(finiteNumber(s.opacity, 1), 0, 1).toFixed(3));
  const hasFilters = brightness !== 0 || contrast !== 0 || saturation !== 0;
  const smooth = s.resampling !== "nearest";

  const layout = rasterLayout(opts);
  const withRenderer = !!layout && (layout.kind === "rgb8" ? opacity < 1 : opts.target === "live" || hasFilters || opacity < 1);
  const worthWriting = withRenderer || hasFilters || (smooth && (opts.target === "live" || s.resampling === "linear"));
  if (!worthWriting) return "";

  const filters = hasFilters
    ? `<brightnesscontrast brightness="${brightness}" contrast="${contrast}" gamma="1"/>` +
      `<huesaturation saturation="${saturation}" grayscaleMode="0" invertColors="0" colorizeOn="0" ` +
      `colorizeRed="255" colorizeGreen="128" colorizeBlue="128" colorizeStrength="100"/>`
    : "";
  return `<pipe>${withRenderer && layout ? rasterRendererXml(layout, opacity) : ""}${filters}${smooth ? BILINEAR : ""}</pipe>`;
}

/**
 * The pipe for a layer whose styling is already painted into its pixels —
 * `qgis-export`'s `qgis_styled` VRT, whose colour bands carry the web map's
 * paint and whose band 4 carries its opacity. Only smoothing is added:
 * restating the style would apply it twice, and QGIS finds the VRT's alpha band
 * itself.
 */
export function geostylerToQgisPaintedRasterPipe(symbolizer: RasterSymbolizer | undefined): string {
  return (symbolizer as Record<string, unknown> | undefined)?.resampling === "nearest" ? "" : `<pipe>${BILINEAR}</pipe>`;
}
